/**
 * @file AvitoFinanceBalanceTab.tsx
 * @description Таб «Баланс» (phase14.0.4): профиль + 3 тайла (real / bonus / CPA).
 *
 * Клик по тайлу CPA → переход на таб «Операции» с фильтром serviceType=cpa.
 */

import { ExternalLink, Mail, Phone, User2, Wallet } from "lucide-react";

import { Alert } from "../../../../components/ui/alert";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import {
  formatPennyToRub,
  type AvitoFinanceSnapshotDto,
} from "../../../../lib/api-shop-avito-finance";

interface Props {
  snapshot: AvitoFinanceSnapshotDto | null;
  onOpenCpaOperations: () => void;
}

export function AvitoFinanceBalanceTab({ snapshot, onOpenCpaOperations }: Props) {
  if (!snapshot || !snapshot.configured) {
    return (
      <Alert severity="info">
        Профиль Avito ещё не загружен. Нажмите «Получить с Avito» вверху страницы
        и отметьте «Профиль» + «Баланс».
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Карточка профиля продавца */}
      <EditorialCard>
        <EditorialCard.Header
          title="Профиль продавца"
          subtitle={
            snapshot.lastFetchedAt
              ? `Обновлено: ${new Date(snapshot.lastFetchedAt).toLocaleString("ru-RU")}`
              : undefined
          }
        />
        <EditorialCard.Body className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field icon={<User2 className="size-4" />} label="Имя" value={snapshot.name ?? "—"} />
          <Field
            icon={<Mail className="size-4" />}
            label="Email"
            value={snapshot.email ?? "—"}
          />
          <Field icon={<Phone className="size-4" />} label="Телефон" value={snapshot.phone ?? "—"} />
          <Field
            icon={<User2 className="size-4" />}
            label="ID продавца"
            value={snapshot.sellerUserId ?? "—"}
          />
          {snapshot.profileUrl && (
            <a
              href={snapshot.profileUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="sm:col-span-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <ExternalLink className="size-4" />
              Открыть профиль на Avito
            </a>
          )}
        </EditorialCard.Body>
      </EditorialCard>

      {/* Тайлы балансов */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <BalanceTile
          icon={<Wallet className="size-5" />}
          label="Основной кошелёк"
          value={formatPennyToRub(snapshot.walletRealPenny)}
          subline={
            snapshot.balanceFetchedAt
              ? `на ${new Date(snapshot.balanceFetchedAt).toLocaleString("ru-RU")}`
              : "не запрашивали"
          }
        />
        <BalanceTile
          icon={<Wallet className="size-5" />}
          label="Бонусные баллы"
          value={formatPennyToRub(snapshot.walletBonusPenny)}
          subline={
            snapshot.balanceFetchedAt
              ? `на ${new Date(snapshot.balanceFetchedAt).toLocaleString("ru-RU")}`
              : "не запрашивали"
          }
        />
        <BalanceTile
          icon={<Wallet className="size-5" />}
          label="CPA-аванс (вычисляемый)"
          value={formatPennyToRub(snapshot.cpaBalancePenny)}
          subline="сумма внесений − списания (по операциям)"
          onClick={onOpenCpaOperations}
          actionHint="Открыть операции"
        />
      </div>
    </div>
  );
}

function Field({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="text-on-surface-variant mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-on-surface-variant">{label}</div>
        <div className="text-sm font-medium text-on-surface break-words">{value}</div>
      </div>
    </div>
  );
}

function BalanceTile({
  icon,
  label,
  value,
  subline,
  onClick,
  actionHint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subline: string;
  onClick?: () => void;
  actionHint?: string;
}) {
  const isClickable = onClick !== undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isClickable}
      className={[
        "border border-outline-variant rounded-lg p-4 text-left flex flex-col gap-2",
        isClickable
          ? "hover:border-primary hover:bg-surface-low transition-colors cursor-pointer"
          : "cursor-default",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 text-on-surface-variant text-xs">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold text-on-surface">{value}</div>
      <div className="text-xs text-on-surface-variant">{subline}</div>
      {actionHint && (
        <div className="text-xs text-primary">{actionHint} →</div>
      )}
    </button>
  );
}
